import React, { useEffect } from 'react';
import { Form, Input, Button, Switch, InputNumber, Card, Tabs, message, Select, Upload, Collapse, Row, Col, Space, Divider } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/axios';
import { SaveOutlined, ArrowLeftOutlined, UploadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import RichTextEditor from '../../../components/common/RichTextEditor';

const { TextArea } = Input;

const convertToHtml = (text?: string): string => {
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) {
        return text;
    }
    return text
        .split(/\n\s*\n/)
        .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br />')}</p>`)
        .join('');
};

export const OlePageEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();

    const { data: pageData, isLoading } = useQuery({
        queryKey: ['adminOlePage', id],
        queryFn: async () => {
            if (!isEditing) return null;
            const res = await apiClient.get(`/admin/ole/pages/${id}`);
            return res.data;
        },
        enabled: isEditing
    });

    useEffect(() => {
        if (pageData && pageData.succeeded) {
            const pd = pageData.data;
            
            // Format translations for AntD Form
            const formattedTranslations: any = {};
            pd.translations?.forEach((t: any) => {
                let parsedAdditional: any = {};
                try {
                    if (t.additionalData) {
                        parsedAdditional = JSON.parse(t.additionalData);
                    }
                } catch (e) {
                    console.error("Failed to parse additionalData JSON", e);
                }

                formattedTranslations[t.languageCode] = {
                    title: t.title,
                    content: convertToHtml(t.content),
                    rightTitle: t.rightTitle,
                    rightContent: convertToHtml(t.rightContent),
                    seoDescription: t.seoDescription,
                    additionalData: t.additionalData,
                    // Parse sub-properties
                    sectionType: parsedAdditional.sectionType || 'default',
                    backgroundImage: parsedAdditional.backgroundImage || '',
                    heroImage: parsedAdditional.heroImage || '',
                    popupImage: parsedAdditional.popupImage || '',
                    heroSubtitle: parsedAdditional.heroSubtitle || '',
                    ctaText: parsedAdditional.ctaText || '',
                    ctaUrl: parsedAdditional.ctaUrl || parsedAdditional.ctaTarget || '',
                    steps: parsedAdditional.steps || [],
                    parentSlug: parsedAdditional.parentSlug || '',
                    icon: parsedAdditional.icon || '',
                    showContactCta: parsedAdditional.showContactCta ?? false,
                    showVoucherInfo: parsedAdditional.showVoucherInfo ?? false,
                    whatWeLookForTitle: parsedAdditional.whatWeLookForTitle || '',
                    whatWeLookFor: parsedAdditional.whatWeLookFor || [],
                    whatWeOfferTitle: parsedAdditional.whatWeOfferTitle || '',
                    whatWeOffer: parsedAdditional.whatWeOffer || [],
                    ctaButtonText: parsedAdditional.ctaButtonText || '',
                    ctaEmail: parsedAdditional.ctaEmail || '',
                    showMap: parsedAdditional.showMap ?? false,
                    showForm: parsedAdditional.showForm ?? false,
                    mapEmbedUrl: parsedAdditional.mapEmbedUrl || '',
                    useRawJson: false
                };
            });

            setTimeout(() => {
                form.setFieldsValue({
                    slug: pd.slug,
                    order: pd.order,
                    isActive: pd.isActive,
                    translations: formattedTranslations
                });
            }, 0);
        }
    }, [pageData, form]);

    const handleCustomRequest = async (options: any, fieldName: any[]) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedUrl = response.data.data.url;
            
            const currentValues = form.getFieldsValue();
            const newTranslations = { ...currentValues.translations };
            const lang = fieldName[1];
            const field = fieldName[2];
            if (!newTranslations[lang]) newTranslations[lang] = {};
            newTranslations[lang][field] = uploadedUrl;
            
            form.setFieldsValue({ translations: newTranslations });
            message.success('Görsel başarıyla yüklendi.');
            onSuccess(null, file);
        } catch (err: any) {
            message.error(err.response?.data?.detail || 'Görsel yükleme hatası.');
            onError(err);
        }
    };

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = tabItems.map(tab => {
                const lang = tab.key;
                const transVal = values.translations?.[lang] || {};

                let additionalObj: any = {};
                const sectionType = transVal.sectionType || 'default';
                
                additionalObj.sectionType = sectionType;
                if (transVal.backgroundImage) {
                    additionalObj.backgroundImage = transVal.backgroundImage;
                }

                if (sectionType === 'hero') {
                    if (transVal.heroImage) additionalObj.heroImage = transVal.heroImage;
                    if (transVal.popupImage) additionalObj.popupImage = transVal.popupImage;
                    if (transVal.heroSubtitle) additionalObj.heroSubtitle = transVal.heroSubtitle;
                    if (transVal.ctaText) additionalObj.ctaText = transVal.ctaText;
                    if (transVal.ctaUrl) {
                        additionalObj.ctaText = transVal.ctaText || 'Mehr erfahren';
                        additionalObj.ctaUrl = transVal.ctaUrl;
                    }
                } else if (sectionType === 'services-overview') {
                    additionalObj.steps = (transVal.steps || []).map((step: any) => ({
                        step: step.step || '',
                        title: step.title || '',
                        description: step.description || '',
                        iconName: step.iconName || '',
                        slug: step.slug || ''
                    }));
                    if (transVal.ctaText) {
                        additionalObj.ctaText = transVal.ctaText;
                        additionalObj.ctaUrl = transVal.ctaUrl || '';
                    }
                } else if (sectionType === 'service-detail') {
                    if (transVal.parentSlug) additionalObj.parentSlug = transVal.parentSlug;
                    if (transVal.icon) additionalObj.icon = transVal.icon;
                } else if (sectionType === 'pricing') {
                    additionalObj.showContactCta = transVal.showContactCta ?? false;
                    if (transVal.ctaText) additionalObj.ctaText = transVal.ctaText;
                    if (transVal.ctaUrl) additionalObj.ctaUrl = transVal.ctaUrl;
                } else if (sectionType === 'but-info') {
                    additionalObj.showVoucherInfo = transVal.showVoucherInfo ?? false;
                } else if (sectionType === 'careers') {
                    if (transVal.whatWeLookForTitle) additionalObj.whatWeLookForTitle = transVal.whatWeLookForTitle;
                    additionalObj.whatWeLookFor = transVal.whatWeLookFor || [];
                    if (transVal.whatWeOfferTitle) additionalObj.whatWeOfferTitle = transVal.whatWeOfferTitle;
                    additionalObj.whatWeOffer = transVal.whatWeOffer || [];
                    if (transVal.ctaText) additionalObj.ctaText = transVal.ctaText;
                    if (transVal.ctaButtonText) additionalObj.ctaButtonText = transVal.ctaButtonText;
                    if (transVal.ctaEmail) additionalObj.ctaEmail = transVal.ctaEmail;
                } else if (sectionType === 'contact') {
                    additionalObj.showMap = transVal.showMap ?? false;
                    additionalObj.showForm = transVal.showForm ?? false;
                    if (transVal.mapEmbedUrl) additionalObj.mapEmbedUrl = transVal.mapEmbedUrl;
                }

                const useRawJson = transVal.useRawJson ?? false;
                let additionalDataStr = '';
                if (useRawJson) {
                    additionalDataStr = transVal.additionalData || '';
                } else {
                    additionalDataStr = JSON.stringify(additionalObj);
                }

                return {
                    languageCode: lang,
                    title: transVal.title || '',
                    content: transVal.content || '',
                    rightTitle: transVal.rightTitle || '',
                    rightContent: transVal.rightContent || '',
                    seoDescription: transVal.seoDescription || '',
                    additionalData: additionalDataStr
                };
            });

            const payload = {
                slug: values.slug,
                order: values.order,
                isActive: values.isActive,
                translations
            };

            if (isEditing) {
                return apiClient.put(`/admin/ole/pages/${id}`, { id, ...payload });
            } else {
                return apiClient.post('/admin/ole/pages', payload);
            }
        },
        onSuccess: () => {
            message.success(isEditing ? 'Sayfa güncellendi' : 'Sayfa oluşturuldu');
            queryClient.invalidateQueries({ queryKey: ['adminOlePages'] });
            navigate('/admin/ole/pages');
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.detail || 'Hata oluştu');
        }
    });

    const onFinish = () => {
        const values = form.getFieldsValue(true);
        mutation.mutate(values);
    };

    const tabItems = [
        { key: 'de', label: 'Almanca (DE)' },
        { key: 'en', label: 'İngilizce (EN)' },
        { key: 'tr', label: 'Türkçe (TR)' }
    ];

    const renderSectionSpecificFields = (lang: string, sectionType: string) => {
        switch (sectionType) {
            case 'hero':
                return (
                    <Card size="small" title="Kahraman Bölümü Ayarları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Kahraman Resmi (Sol Görsel)">
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Form.Item name={['translations', lang, 'heroImage']} noStyle>
                                            <Input placeholder="/media/..." />
                                        </Form.Item>
                                        <Upload 
                                            customRequest={(opts) => handleCustomRequest(opts, ['translations', lang, 'heroImage'])}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />}>Yükle</Button>
                                        </Upload>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Açılır Pencere (Popup) Resmi">
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Form.Item name={['translations', lang, 'popupImage']} noStyle>
                                            <Input placeholder="/media/..." />
                                        </Form.Item>
                                        <Upload 
                                            customRequest={(opts) => handleCustomRequest(opts, ['translations', lang, 'popupImage'])}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />}>Yükle</Button>
                                        </Upload>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name={['translations', lang, 'heroSubtitle']} label="Kahraman Alt Başlığı">
                                    <Input placeholder="Örn: Mit Liebe gelehrt, ewig bewahrt" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'ctaText']} label="Eylem Butonu Metni">
                                    <Input placeholder="Örn: Şimdi Başvur" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'ctaUrl']} label="Eylem Yönlendirme Linki (CTA URL)">
                                    <Input placeholder="Örn: /#kontakt" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                );
            case 'services-overview':
                return (
                    <Card size="small" title="Wege zum Wissen (Bilgiye Giden Yollar) Adımları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['translations', lang, 'ctaText']} label="Eylem Butonu Metni (Opsiyonel)">
                                        <Input placeholder="Örn: Daha fazla bilgi" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['translations', lang, 'ctaUrl']} label="Eylem Butonu Yönlendirme Linki">
                                        <Input placeholder="Örn: /#kontakt" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                        <Form.List name={['translations', lang, 'steps']}>
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Card size="small" key={key} style={{ marginBottom: '1rem', border: '1px dashed #d9d9d9' }}
                                            extra={<Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(name)}>Sil</Button>}
                                            title={`Adım #${name + 1}`}
                                        >
                                            <Row gutter={16}>
                                                <Col span={6}>
                                                    <Form.Item {...restField} name={[name, 'step']} label="Numara" rules={[{ required: true, message: 'Gerekli' }]}>
                                                        <Input placeholder="Örn: 01" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={10}>
                                                    <Form.Item {...restField} name={[name, 'title']} label="Başlık" rules={[{ required: true, message: 'Gerekli' }]}>
                                                        <Input placeholder="Adım Başlığı" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item {...restField} name={[name, 'iconName']} label="İkon" rules={[{ required: true, message: 'Gerekli' }]}>
                                                        <Select placeholder="İkon seçin">
                                                            <Select.Option value="UserCheck">Bireysel Ders (UserCheck)</Select.Option>
                                                            <Select.Option value="Users">Grup Dersi (Users)</Select.Option>
                                                            <Select.Option value="Laptop">Online Ders (Laptop)</Select.Option>
                                                            <Select.Option value="GraduationCap">Danışmanlık (GraduationCap)</Select.Option>
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col span={16}>
                                                    <Form.Item {...restField} name={[name, 'description']} label="Açıklama" rules={[{ required: true, message: 'Gerekli' }]}>
                                                        <TextArea rows={2} placeholder="Kısa açıklama..." />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item {...restField} name={[name, 'slug']} label="Sayfa Linki (Slug)" rules={[{ required: true, message: 'Gerekli' }]}>
                                                        <Input placeholder="Örn: /einzelunterricht" />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Yeni Adım Ekle
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Card>
                );
            case 'service-detail':
                return (
                    <Card size="small" title="Hizmet Detay Sayfası Ayarları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'parentSlug']} label="Üst Sayfa Linki (Slug)">
                                    <Input placeholder="Örn: nachhilfeangebot" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'icon']} label="İkon Kodu">
                                    <Input placeholder="Örn: user, users, monitor, globe" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                );
            case 'pricing':
                return (
                    <Card size="small" title="Fiyatlandırma Bölümü Ayarları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item name={['translations', lang, 'showContactCta']} valuePropName="checked" label="CTA Butonunu Göster">
                                    <Switch />
                                </Form.Item>
                            </Col>
                            <Col span={9}>
                                <Form.Item name={['translations', lang, 'ctaText']} label="Buton Metni">
                                    <Input placeholder="Örn: Şimdi Başvur" />
                                </Form.Item>
                            </Col>
                            <Col span={9}>
                                <Form.Item name={['translations', lang, 'ctaUrl']} label="Buton Linki (URL)">
                                    <Input placeholder="Örn: /#kontakt" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                );
            case 'but-info':
                return (
                    <Card size="small" title="Eğitim ve Katılım (BuT) Bölümü Ayarları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <Form.Item name={['translations', lang, 'showVoucherInfo']} valuePropName="checked" label="Kupon (Gutschein) Bilgilerini Göster">
                            <Switch />
                        </Form.Item>
                    </Card>
                );
            case 'careers':
                return (
                    <Card size="small" title="Kariyer / Ekibimiz Bölümü Ayarları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'whatWeLookForTitle']} label="Aradığımız Özellikler Başlığı">
                                    <Input placeholder="Aradığımız Özellikler" />
                                </Form.Item>
                                <Form.List name={['translations', lang, 'whatWeLookFor']}>
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map((field) => (
                                                <Form.Item required={false} key={field.key} style={{ marginBottom: '8px' }}>
                                                    <Space style={{ display: 'flex', width: '100%' }}>
                                                        <Form.Item {...field} noStyle>
                                                            <Input placeholder="Örn: İletişim becerisi yüksek" style={{ width: '280px' }} />
                                                        </Form.Item>
                                                        <Button type="link" danger onClick={() => remove(field.name)} icon={<DeleteOutlined />} />
                                                    </Space>
                                                </Form.Item>
                                            ))}
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small">
                                                Özellik Ekle
                                            </Button>
                                        </>
                                    )}
                                </Form.List>
                            </Col>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'whatWeOfferTitle']} label="Sunduklarımız Başlığı">
                                    <Input placeholder="Sunduklarımız" />
                                </Form.Item>
                                <Form.List name={['translations', lang, 'whatWeOffer']}>
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map((field) => (
                                                <Form.Item required={false} key={field.key} style={{ marginBottom: '8px' }}>
                                                    <Space style={{ display: 'flex', width: '100%' }}>
                                                        <Form.Item {...field} noStyle>
                                                            <Input placeholder="Örn: Esnek çalışma saatleri" style={{ width: '280px' }} />
                                                        </Form.Item>
                                                        <Button type="link" danger onClick={() => remove(field.name)} icon={<DeleteOutlined />} />
                                                    </Space>
                                                </Form.Item>
                                            ))}
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small">
                                                İmkan Ekle
                                            </Button>
                                        </>
                                    )}
                                </Form.List>
                            </Col>
                        </Row>
                        <Divider style={{ margin: '12px 0' }} />
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={['translations', lang, 'ctaText']} label="CTA Açıklama Metni">
                                    <Input placeholder="Başvuru açıklaması..." />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={['translations', lang, 'ctaButtonText']} label="CTA Buton Yazısı">
                                    <Input placeholder="Örn: Şimdi Başvur" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={['translations', lang, 'ctaEmail']} label="Başvuru E-postası">
                                    <Input placeholder="Örn: nachhilfe@ole-lernzentrum.de" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                );
            case 'contact':
                return (
                    <Card size="small" title="İletişim Bölümü Ayarları" style={{ marginBottom: '1.5rem', background: '#fafafa' }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item name={['translations', lang, 'showMap']} valuePropName="checked" label="Haritayı Göster">
                                    <Switch />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name={['translations', lang, 'showForm']} valuePropName="checked" label="İletişim Formunu Göster">
                                    <Switch />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name={['translations', lang, 'mapEmbedUrl']} label="Google Harita Embed URL">
                                    <TextArea rows={2} placeholder="https://www.google.com/maps/embed?..." />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                );
            default:
                return null;
        }
    };

    if (isLoading) return <div>Yükleniyor...</div>;

    return (
        <Card title={isEditing ? "Sayfayı Düzenle" : "Yeni Sayfa Ekle"} extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/ole/pages')}>Geri</Button>}>
            <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ isActive: true, order: 1 }}>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <Form.Item name="slug" label="Slug (URL Yolu)" rules={[{ required: true, message: 'Slug zorunludur' }]}>
                        <Input placeholder="ornek-sayfa" />
                    </Form.Item>
                    
                    <Form.Item name="order" label="Sıra" rules={[{ required: true }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="isActive" label="Aktif mi?" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </div>

                <Tabs defaultActiveKey="de" items={tabItems.map(tab => ({
                    key: tab.key,
                    label: tab.label,
                    children: (
                        <div style={{ padding: '1rem 0' }}>
                            <Form.Item name={['translations', tab.key, 'title']} label="Sol Kolon Başlığı (Ana Başlık)" rules={tab.key === 'de' ? [{ required: true, message: 'Almanca başlık zorunludur' }] : []}>
                                <Input placeholder={`${tab.key.toUpperCase()} Başlık`} />
                            </Form.Item>
                            
                            <Form.Item name={['translations', tab.key, 'content']} label="Sol Kolon İçeriği (Ana İçerik)">
                                <RichTextEditor 
                                    key={`left_${tab.key}_${pageData?.data?.id || 'new'}`}
                                    placeholder={`${tab.key.toUpperCase()} Sol İçerik`} 
                                />
                            </Form.Item>

                            <Form.Item name={['translations', tab.key, 'rightTitle']} label="Sağ Kolon Başlığı (Opsiyonel)">
                                <Input placeholder={`${tab.key.toUpperCase()} Sağ Başlık (Boş bırakılırsa ana başlık kullanılır)`} />
                            </Form.Item>

                            <Form.Item name={['translations', tab.key, 'rightContent']} label="Sağ Kolon İçeriği (Opsiyonel)">
                                <RichTextEditor 
                                    key={`right_${tab.key}_${pageData?.data?.id || 'new'}`}
                                    placeholder={`${tab.key.toUpperCase()} Sağ İçerik`} 
                                />
                            </Form.Item>

                            <Form.Item name={['translations', tab.key, 'seoDescription']} label="SEO Açıklaması">
                                <TextArea rows={3} placeholder="Arama motorları için kısa açıklama" />
                            </Form.Item>

                            <Divider style={{ margin: '24px 0' }} />
                            <h3>Sayfa Tasarımı ve Ek Veriler</h3>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <Form.Item name={['translations', tab.key, 'sectionType']} label="Bölüm/Sayfa Şablon Tipi">
                                    <Select placeholder="Sayfa şablonunu seçin">
                                        <Select.Option value="default">Varsayılan (Düz İçerik)</Select.Option>
                                        <Select.Option value="hero">Kahraman Bölümü (Giriş/Slayt Altı)</Select.Option>
                                        <Select.Option value="services-overview">Hizmetler Genel Bakış (Wege zum Wissen)</Select.Option>
                                        <Select.Option value="service-detail">Hizmet Detayı (Örn: Bireysel Ders)</Select.Option>
                                        <Select.Option value="pricing">Fiyatlandırma Bölümü</Select.Option>
                                        <Select.Option value="but-info">BuT (Eğitim ve Katılım)</Select.Option>
                                        <Select.Option value="careers">Kariyer (Werde Teil unseres Teams)</Select.Option>
                                        <Select.Option value="contact">İletişim Bölümü (Harita & Form)</Select.Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item label="Arka Plan Görseli">
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Form.Item name={['translations', tab.key, 'backgroundImage']} noStyle>
                                            <Input placeholder="/media/..." />
                                        </Form.Item>
                                        <Upload 
                                            customRequest={(opts) => handleCustomRequest(opts, ['translations', tab.key, 'backgroundImage'])}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />}>Yükle</Button>
                                        </Upload>
                                    </Space.Compact>
                                </Form.Item>
                            </div>

                            <Form.Item noStyle dependencies={[['translations', tab.key, 'sectionType']]}>
                                {() => {
                                    const sectionType = form.getFieldValue(['translations', tab.key, 'sectionType']) || 'default';
                                    return renderSectionSpecificFields(tab.key, sectionType);
                                }}
                            </Form.Item>

                            <Divider style={{ margin: '24px 0' }} />
                            
                            <Collapse ghost size="small">
                                <Collapse.Panel key="raw_json" header="Gelişmiş Seçenekler: Ham JSON Verisi">
                                    <Form.Item name={['translations', tab.key, 'useRawJson']} valuePropName="checked" label="JSON Verisini Doğrudan Kullan (Yukarıdaki form alanlarını yok sayar)">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['translations', tab.key, 'additionalData']} label="Ek Veri (JSON)">
                                        <TextArea rows={4} style={{ fontFamily: 'monospace' }} placeholder='{"heroImage": "/media/...", "sectionType": "hero"}' />
                                    </Form.Item>
                                </Collapse.Panel>
                            </Collapse>
                        </div>
                    )
                }))} />

                <Form.Item style={{ marginTop: '20px' }}>
                    <Button type="primary" htmlType="submit" loading={mutation.isPending} icon={<SaveOutlined />}>
                        {isEditing ? 'Güncelle' : 'Kaydet'}
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};
